<template>
  <!-- 
      PvP遊戲多點觸控優化樣式：
      - touch-action: none        → 禁用瀏覽器預設觸控手勢（縮放、滾動），確保多點觸控正常
      - user-select: none         → 防止觸控時選取文字，避免干擾遊戲
      - -webkit-touch-callout     → 禁用 iOS 長按選單（複製、貼上等）
      - -webkit-user-select       → iOS Safari 舊版兼容性，防止文字選取
  -->
  <div class="pvpgame-container" style="touch-action: none; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">


    <!-- 游戏区域 -->
    <div class="game-area" ref="gameAreaRef"
      :style="{ 'background': mockImg, 'background-size': 'cover', 'background-repeat': 'no-repeat' }">
     

      <div class="game-header">
        <div class="game-topic">{{ gameConfig.topic }}</div>
      </div>
      <div class="timer">{{ formatTime(timeRemaining) }}
        <button class="start-button" @click="toggleGame">
          <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
        </button>
      </div>

      <!-- 遊戲開始前彈窗區 -->
      <div v-if="beforeGame" class="before-game-container">
        <div class="before-game-modal">
          <div class="game-title">{{ gameConfig.topic }}</div>
          <div class="game-info">
            <div class="game-time">{{ $t('PvPgame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('PvPgame.second') }}</div>
            <div class="game-speed">{{ $t('PvPgame.gameSpeed') }}： {{ getSpeedText(gameConfig.gameSpeed) }}</div>
          </div>
          <div class="game-buttons">
            <button class="action-button play-button" @click="showReadyGoCountdown()">
              <svg-icon icon-class="play" class="play-icon"/>
            </button>
            <button v-if="!hasConfigUrl" class="action-button setting-button" @click="goToSettings()">
              <svg-icon icon-class="setting-game" class="setting-icon"/>
            </button>
            <!-- WebView 環境才顯示推送按鈕 -->
            <button v-if="isWebView" class="action-button push-button" 
                    :class="{ 'disabled': isSendDisabled }"
                    @click="handleSendClick()">
              <svg-icon icon-class="upload" class="sendweb-icon" />
            </button>
          </div>
        </div>
      </div>

      
      <!-- Ready, Go! 提示 -->
      <div v-if="showReadyGo" class="ready-go-container">
        <div class="ready-go-text">{{ readyGoText }}</div>
      </div>

      <!-- 遊戲暫停彈窗 -->
      <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
        <div class="pause-game-modal">
          <div class="pause-title">{{ $t('PvPgame.gamePaused') || '活動暫停' }}</div>
          <div class="pause-buttons">
            <button class="action-button continue-button" @click="resumeGame()">
              <svg-icon icon-class="play"  class="play-icon"/>
            </button>
            <button class="action-button restart-button" @click="resetGame()">
              <svg-icon icon-class="refresh" class="refresh-icon" />
            </button>
          </div>
        </div>
      </div>

      <!-- 左侧游戏区域 -->
      <div class="game-side left-side">
        <div class="left-score">{{ leftScore }}</div>
        <div v-for="(item, index) in leftItems" :key="'left-' + index" class="falling-item left-item"
          :style="{ left: item.x + 'px', top: item.y + 'px', backgroundImage: leftBackground }" 
          @click="handleItemClick(item, 'left')"
          @touchstart="handleTouchStart($event, item, 'left')"
          @touchend="handleTouchEnd($event)">
          {{ item.text }}
        </div>
      </div>

      <!-- 右侧游戏区域 -->
      <div class="game-side right-side">
        <div class="right-score">{{ rightScore }}</div>
        <div v-for="(item, index) in rightItems" :key="'right-' + index" class="falling-item right-item"
          :style="{ left: item.x + 'px', top: item.y + 'px', backgroundImage: rightBackground }" 
          @click="handleItemClick(item, 'right')"
          @touchstart="handleTouchStart($event, item, 'right')"
          @touchend="handleTouchEnd($event)">
          {{ item.text }}
        </div>
      </div>

      <!-- 飛行分數動畫 -->
      <transition-group name="flying-score" tag="div">
        <div
          v-for="effect in flyingScoreEffects"
          :key="effect.id"
          class="flying-score-effect"
          :style="{
            left: effect.currentX + 'px',
            top: effect.currentY + 'px',
            color: effect.isCorrect ? 'blue' : 'red',
            opacity: effect.opacity
          }"
        >
          {{ effect.text }}
        </div>
      </transition-group>

    <!-- 遊戲結束彈窗 -->
    <div v-if="gameOver" class="game-over-modal">
      <div class="game-result-container" v-show="!showAnswerModal">
        <!-- 左側結果卡片 -->
        <div class="result-card-wrap">
          <div class="win-act" v-show="leftScore > rightScore">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
        <div class="result-card left-result" >
          <div class="result-header">
            <div class="result-title" :class="{ 'win-title': leftScore > rightScore, 'tie-title': leftScore === rightScore, 'lose-title': leftScore < rightScore   }">
              {{ leftScore > rightScore ? $t('PvPgame.win') : (leftScore === rightScore ? $t('PvPgame.tie') : '') }}
            </div>
          </div>
          <div class="result-score">
            <div class="star-score">
              <span class="score-number">{{ leftScore }}</span>
            </div>
          </div>
          <div class="result-details">
            <div class="detail-item">
              {{ $t('PvPgame.correctItems') }}: {{ leftCorrectCount }} {{ $t('PvPgame.items') }}
            </div>
            <div class="detail-item">
              {{ $t('PvPgame.errorItems') }}: {{ leftWrongCount }} {{ $t('PvPgame.items') }}
            </div>
            <!-- <div class="detail-item">
              {{ $t('PvPgame.maxCombo') }}: {{ leftMaxCombo }} {{ $t('PvPgame.items') }}
            </div> -->
          </div>
        </div>
        </div>

        <!-- 右側結果卡片 -->
         <div class="result-card-wrap" v-show="!showAnswerModal">
          <div class="win-act" v-show="leftScore < rightScore">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
        <div class="result-card right-result" >
         
          <div class="result-header">
            <div class="result-title" :class="{ 'win-title': rightScore > leftScore, 'tie-title': rightScore === leftScore, 'lose-title': rightScore < leftScore }">
              {{ rightScore > leftScore ? $t('PvPgame.win') : (rightScore === leftScore ? $t('PvPgame.tie') : '') }}
            </div>
          </div>
          <div class="result-score">
            <div class="star-score">
              <span class="score-number">{{ rightScore }}</span>
            </div>
          </div>
          <div class="result-details">
            <div class="detail-item">
              {{ $t('PvPgame.correctItems') }}: {{ rightCorrectCount }} {{ $t('PvPgame.items') }}
            </div>
            <div class="detail-item">
              {{ $t('PvPgame.errorItems') }}: {{ rightWrongCount }} {{ $t('PvPgame.items') }}
            </div>
            <!-- <div class="detail-item">
              {{ $t('PvPgame.maxCombo') }}: {{ rightMaxCombo }} {{ $t('PvPgame.items') }}
            </div> -->
          </div>
          </div>
        </div>
      </div>
      <!-- 答案彈窗 -->
      <div v-if="showAnswerModal" class="answer-modal-mask">
          <div class="answer-modal-card">
            <div class="answer-modal-header">
              <span class="answer-modal-title">{{ $t('PvPgame.referenceAnswers') }}</span>
              <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
            </div>
            <div class="answer-modal-section">
              <div class="answer-label correct">✔</div>
              <div class="answer-list">
                <span v-for="(ans, idx) in gameConfig.correctVocabularyList" :key="'c-'+idx" class="answer-item correct">{{ ans }}</span>
              </div>
            </div>
            <div class="answer-modal-section">
              <div class="answer-label wrong">✘</div>
              <div class="answer-list">
                <span v-for="(ans, idx) in gameConfig.errorVocabularyList" :key="'e-'+idx" class="answer-item wrong">{{ ans }}</span>
              </div>
            </div>
          </div>
        </div>

      <!-- 底部按鈕區 -->
      <div class="game-result-actions" v-show="!showAnswerModal">
        <button class="action-button answer-button" @click="toggleAnswerModal">
          <span class="answer-text">{{ $t('PvPgame.showAnswers') }}</span>
        </button>
        <button class="action-button restart-button" @click="resetGame">
          <svg-icon icon-class="refresh" class="refresh-icon" />
        </button>
      </div>
    </div>
  </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed, getCurrentInstance, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';

const router = useRouter();
const that = getCurrentInstance().proxy;
const { t } = useI18n();
const iviewMessage = inject("$Message");

// 根據速度值獲取對應的語系文字
const getSpeedText = (speed) => {
  if (speed === 3) return that.$t('PvPgame.speedFast');
  if (speed === 2) return that.$t('PvPgame.speedMedium');
  if (speed === 1) return that.$t('PvPgame.speedSlow');
  return speed;
};

// 音效參考
// 背景音樂
const backgroundMusic = ref(null);
// 正確音效
const correctSound = ref(null);
// 錯誤音效
const wrongSound = ref(null);
// 平局音效
const tieSound = ref(null);
// 獲勝音效
const winSound = ref(null);
// Ready 音效
const readySound = ref(null);
// Go 音效
const goSound = ref(null);

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', event => {
                callback(event);
            });
        } else {
            window.addEventListener('message', event => {
                callback(event);
            });
        }
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// 推送按鈕冷卻狀態
const isSendDisabled = ref(false);
const cooldownTimer = ref(null);

// 配置加載狀態
const hasConfigUrl = ref(false);
const configUrl = ref('');

let gameConfig = reactive({
  topic: 'empty state',
  correctVocabularyList: [],
  errorVocabularyList: [],
  gameDuration: 0,
  background: ''
});

// 從 blob URL 載入配置
const loadConfigFromUrl = async (url) => {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error('載入失敗');
        }
        
        const config = await response.json();
        Object.assign(gameConfig, JSON.parse(config.data.gameConfig));
        timeRemaining.value = gameConfig.gameDuration;
        
        iviewMessage.success({
            content: t('PvPgame.configLoadSuccess'),
            duration: 2,
            closable: true
        });
    } catch (error) {
        iviewMessage.error({
            content: t('PvPgame.configLoadFailed'),
            duration: 3,
            closable: true
        });
        
        // 使用預設配置
        Object.assign(gameConfig, {
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "note", "notebook", "notebook", "notebook", "notebook", "notebook", "notebook"],
            gameDuration: 40,
            background: "desert",
            gameSpeed: 2
        });
        timeRemaining.value = gameConfig.gameDuration;
    }
};

onMounted(async () => {
    // 檢查是否有 config_url 參數 - 使用原本的方式
    const fullUrl = window.location.href;
    const configUrlIndex = fullUrl.indexOf('config_url=');
    
    if (configUrlIndex !== -1) {
        // 取得 config_url= 之後的所有內容（直到字串結束）
        configUrl.value = fullUrl.substring(configUrlIndex + 'config_url='.length);
        hasConfigUrl.value = true;
        await loadConfigFromUrl(configUrl.value);
    } else {
        hasConfigUrl.value = false;
        
        // 發送初始化訊息，並接收遊戲配置
        messageBridge.postMessage({ action: 'Init.PvPGame', data: { //到時候如果該頁沒有設置就用demo
            topic: "Choose Fruits (Demo)",
            correctVocabularyList: ["apple", "banana", "orange", "grape", "watermelon", "strawberry", "blueberry", "pineapple", "kiwi", "mango"],
            errorVocabularyList: ["pen", "pencil", "notebook", "note", "notebook", "notebook", "notebook", "notebook", "notebook", "notebook"],
            gameDuration: 40,
            background: "desert",
            gameSpeed: 2
        } });
        
        messageBridge.addEventListener(event => {
            const msg = event.data;
            console.log(msg);
            if (msg.action === 'Init.PvPGame') {
                console.log(msg.data, 'Init.PvPGame');
                Object.assign(gameConfig, msg.data);
                // 將設定存入 sessionStorage
                sessionStorage.setItem('PvPGameConfig', JSON.stringify(msg.data));
            }
        });
    }
});



// 遊戲狀態
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const timeRemaining = ref(0);
const leftScore = ref(0);
const rightScore = ref(0);
const gameAreaRef = ref(null);

// 遊戲結果統計
const leftCorrectCount = ref(0);
const leftWrongCount = ref(0);
const leftMaxCombo = ref(0);
const rightCorrectCount = ref(0);
const rightWrongCount = ref(0);
const rightMaxCombo = ref(0);
const leftCurrentCombo = ref(0);
const rightCurrentCombo = ref(0);

// Ready, Go! 相關狀態

// 答案彈窗狀態
const showAnswerModal = ref(false);

// 答案彈窗切換，與 gameOver 互為相反
function toggleAnswerModal() {
showAnswerModal.value = !showAnswerModal.value;
}

const showReadyGo = ref(false);
const readyGoText = ref('');
const isCountingDown = ref(false);

const leftBackground = ref('');
const rightBackground = ref('');

function setRandomBackgrounds() {
  if (gameConfig.background === 'desert') {
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/green_star.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/orange_star.webp')}")`;
  } else if (gameConfig.background === 'ocean') {
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/dark_blue_fish.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/dark_green_fish.webp')}")`;
  } else if (gameConfig.background === 'forest') {
    const leftItems = ['orange', 'red_apple', 'tomato'];
    const rightItems = ['orange', 'red_apple', 'green_lime', 'tomato'];
    let leftRandom = leftItems[Math.floor(Math.random() * leftItems.length)];
    let rightRandom = rightItems[Math.floor(Math.random() * rightItems.length)];
    // 保證左右不一樣
    let tries = 0;
    while (rightRandom === leftRandom && tries < 10) {
      rightRandom = rightItems[Math.floor(Math.random() * rightItems.length)];
      tries++;
    }
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/' + leftRandom + '.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/' + rightRandom + '.webp')}")`;
  } else {
    leftBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/green_star.webp')}")`;
    rightBackground.value = `url("${require('@/assets/HTwbActAssets/PvPgameObj/orange_star.webp')}")`;
  }
}


const mockImg = computed(() => {
  const backgrounds = {
    forest: 'fantasy_forest',
    ocean: 'ocean_world',
    desert: 'desert_oasis',
  };
  return gameConfig.background ? `url("${require('@/assets/HTwbActAssets/' + backgrounds[gameConfig.background] + '.webp')}")` : ''
})

// 掉落物品
const leftItems = ref([]);
const rightItems = ref([]);

// 動畫分數效果
const flyingScoreEffects = ref([]);
let flyingScoreId = 0;

// 游戏循环和计时器
let gameLoop = null;
let timerInterval = null;

// 游戏区域尺寸
let gameAreaWidth = 0;
let gameAreaHeight = 0;

// 遊戲開始前彈窗
const beforeGame = ref(true);

// 預分配的掉落物品佇列
const itemQueues = reactive({
  leftQueue: [],
  rightQueue: []
});

// 遊戲測試統計
const gameStats = reactive({
  totalGenerated: 0,
  totalClicked: 0,
  totalMissed: 0,
  leftQueueStartSize: 0,
  rightQueueStartSize: 0,
  gameStartTime: 0,
  lastReportTime: 0
});

// 記錄左右兩側已生成的正確項和錯誤項數量
const itemCounter = reactive({
  leftCorrect: 0,
  leftError: 0,
  rightCorrect: 0,
  rightError: 0
});

// 預先分配整場遊戲的掉落物品，確保公平性
const preAllocateGameItems = () => {
  // 清空佇列
  itemQueues.leftQueue = [];
  itemQueues.rightQueue = [];
  
  // 根據遊戲時長計算合理的物品總數（保守估計，確保能全部掉完）
  let itemsPerSecond;
  if (gameConfig.gameDuration <= 20) {
    itemsPerSecond = 0.5; // 20秒: 10個/側
  } else if (gameConfig.gameDuration <= 30) {
    itemsPerSecond = 0.5; // 30秒: 15個/側  
  } else {
    itemsPerSecond = 0.45; // 40秒: 18個/側
  }
  const totalItemsPerSide = Math.floor(gameConfig.gameDuration * itemsPerSecond);
  
  // 確保正確和錯誤物品比例（60%正確，40%錯誤）
  const correctItemsPerSide = Math.floor(totalItemsPerSide * 0.6);
  const errorItemsPerSide = totalItemsPerSide - correctItemsPerSide;
  
  // 為左右兩側分配相同數量的正確和錯誤項目
  for (let side of ['left', 'right']) {
    const queue = [];
    
    // 添加正確項目
    for (let i = 0; i < correctItemsPerSide; i++) {
      const randomCorrectItem = gameConfig.correctVocabularyList[
        Math.floor(Math.random() * gameConfig.correctVocabularyList.length)
      ];
      queue.push({
        text: randomCorrectItem,
        isCorrect: true
      });
    }
    
    // 添加錯誤項目
    for (let i = 0; i < errorItemsPerSide; i++) {
      const randomErrorItem = gameConfig.errorVocabularyList[
        Math.floor(Math.random() * gameConfig.errorVocabularyList.length)
      ];
      queue.push({
        text: randomErrorItem,
        isCorrect: false
      });
    }
    
    // 打亂順序
    for (let i = queue.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [queue[i], queue[j]] = [queue[j], queue[i]];
    }
    
    // 分配到對應佇列
    if (side === 'left') {
      itemQueues.leftQueue = queue;
    } else {
      itemQueues.rightQueue = queue;
    }
  }
  
  // 記錄統計資料
  gameStats.leftQueueStartSize = totalItemsPerSide;
  gameStats.rightQueueStartSize = totalItemsPerSide;
  gameStats.totalGenerated = 0;
  gameStats.totalClicked = 0;
  gameStats.totalMissed = 0;
  
  console.log(`📊 預分配完成 - 遊戲時長: ${gameConfig.gameDuration}秒`);
  console.log(`📦 左側: ${totalItemsPerSide}項 (${correctItemsPerSide}正確 + ${errorItemsPerSide}錯誤)`);
  console.log(`📦 右側: ${totalItemsPerSide}項 (${correctItemsPerSide}正確 + ${errorItemsPerSide}錯誤)`);
  console.log(`⏱️ 預期每秒生成: ${(totalItemsPerSide / gameConfig.gameDuration).toFixed(1)}個物品`);
  console.log(`🎯 理論最高分: 左側${correctItemsPerSide * 10}分 | 右側${correctItemsPerSide * 10}分`);
  
  // 顯示詳細分配內容
  console.log(`\n📋 詳細分配清單:`);
  console.log(`✅ 左側正確項目: [${itemQueues.leftQueue.filter(item => item.isCorrect).map(item => item.text).join(', ')}]`);
  console.log(`❌ 左側錯誤項目: [${itemQueues.leftQueue.filter(item => !item.isCorrect).map(item => item.text).join(', ')}]`);
  console.log(`✅ 右側正確項目: [${itemQueues.rightQueue.filter(item => item.isCorrect).map(item => item.text).join(', ')}]`);
  console.log(`❌ 右側錯誤項目: [${itemQueues.rightQueue.filter(item => !item.isCorrect).map(item => item.text).join(', ')}]`);
};

// 生成随機位置，確保不會與現有物體重疊
const getRandomPosition = () => {
  const width = gameAreaWidth / 2 - 240; // 考慮物體寬度及左右邊界
  const itemSize = 150; // 物體寬度
  const minDistance = itemSize + 10; // 物體之間的最小距離

  // 嘗試多次生成不重疊的位置
  for (let attempts = 0; attempts < 20; attempts++) {
    const x = 100 + Math.floor(Math.random() * width);
    const y =-40- Math.random() * 500; // 從頂部開始掉落

    // 檢查是否與左側現有物體重疊
    const leftOverlap = leftItems.value.some(item => {
      return Math.abs(item.x - x) < minDistance && Math.abs(item.y - y) < minDistance;
    });

    // 檢查是否與右側現有物體重疊
    const rightOverlap = rightItems.value.some(item => {
      return Math.abs(item.x - x) < minDistance && Math.abs(item.y - y) < minDistance;
    });

    // 如果沒有重疊，返回這個位置
    if (!leftOverlap && !rightOverlap) {
      return { x, y };
    }
  }

  // 如果多次嘗試後仍無法找到不重疊的位置，則延遲生成
  return null;
};

// 生成新的掉落物品对（從預分配佇列中取出）
const createFallingItemPair = (position) => {
  // 檢查佇列是否還有物品
  if (itemQueues.leftQueue.length === 0 || itemQueues.rightQueue.length === 0) {
    return null; // 沒有更多物品可生成
  }
  
  // 使用傳入的位置，或者生成新的位置
  const leftPosition = position || getRandomPosition();
  if (!leftPosition) return null;

  const rightPosition = position || getRandomPosition();
  if (!rightPosition) return null;
  
  const speed = gameConfig.gameSpeed * 2; // 加大速度倍數，讓速度差異更明顯

  // 從預分配佇列中取出物品（先進先出）
  const leftItem = itemQueues.leftQueue.shift(); // 取出第一個物品
  const rightItem = itemQueues.rightQueue.shift(); // 取出第一個物品

  // 建立兩個物品物件
  const commonId = Date.now() + Math.random();

  const leftFallingItem = {
    id: commonId + '-left',
    text: leftItem.text,
    isCorrect: leftItem.isCorrect,
    x: leftPosition.x,
    y: leftPosition.y,
    speed: speed,
    side: 'left'
  };

  const rightFallingItem = {
    id: commonId + '-right',
    text: rightItem.text,
    isCorrect: rightItem.isCorrect,
    x: rightPosition.x,
    y: rightPosition.y,
    speed: speed,
    side: 'right'
  };

  return { leftFallingItem, rightFallingItem };
};

// 更新掉落物品位置
const updateFallingItems = () => {
  // 更新左侧
  const leftBefore = leftItems.value.length;
  leftItems.value = leftItems.value.filter(item => {
    item.y += item.speed;
    return item.y < gameAreaHeight; // 移除已经掉出屏幕的物品
  });
  const leftMissed = leftBefore - leftItems.value.length;

  // 更新右侧
  const rightBefore = rightItems.value.length;
  rightItems.value = rightItems.value.filter(item => {
    item.y += item.speed;
    return item.y < gameAreaHeight; // 移除已经掉出屏幕的物品
  });
  const rightMissed = rightBefore - rightItems.value.length;
  
  // 統計掉落數量
  gameStats.totalMissed += leftMissed + rightMissed;

  // 同時為兩側添加新物品，使用相同的位置和速度
  // 動態計算生成機率，確保所有預分配物品都能在遊戲時間內掉完
  const remainingTime = timeRemaining.value;
  const remainingItems = Math.max(itemQueues.leftQueue.length, itemQueues.rightQueue.length);
  
  // 如果還有物品要生成且遊戲還在進行
  if (remainingItems > 0 && remainingTime > 0) {
    // 計算每秒需要生成的物品數量來確保全部掉完
    const requiredSpawnRate = remainingItems / remainingTime;
    // 轉換為每50ms的生成機率（遊戲循環頻率），增加更多安全係數
    const spawnProbability = (requiredSpawnRate / 20) * 1.5; // 增加安全係數從1.2到1.5
    
    // 如果剩餘時間不多，增加生成機率確保物品能掉完
    let finalSpawnProbability = spawnProbability;
    if (remainingTime <= 15 && remainingItems > 3) {
      finalSpawnProbability = Math.min(0.9, spawnProbability * 2.5); // 最後15秒更積極加速生成
    } else if (remainingTime <= 5 && remainingItems > 0) {
      finalSpawnProbability = Math.min(1.0, spawnProbability * 4); // 最後5秒全力衝刺
    }
    
    if (Math.random() < finalSpawnProbability && leftItems.value.length < 6 && rightItems.value.length < 6) {
      const position = getRandomPosition();
      if (position) {
        const itemPair = createFallingItemPair(position);
        if (itemPair) {
          leftItems.value.push(itemPair.leftFallingItem);
          rightItems.value.push(itemPair.rightFallingItem);
          gameStats.totalGenerated += 2; // 左右各一個
        }
      }
    }
  }
  
  // 每5秒報告一次進度
  const currentTime = Date.now();
  if (currentTime - gameStats.lastReportTime > 5000) {
    gameStats.lastReportTime = currentTime;
    const remainingTotal = itemQueues.leftQueue.length + itemQueues.rightQueue.length;
    const processedTotal = gameStats.totalClicked + gameStats.totalMissed;
    console.log(`🕐 剩餘時間: ${remainingTime}秒 | 佇列剩餘: ${remainingTotal}項 | 已處理: ${processedTotal}項 (點擊${gameStats.totalClicked} + 掉落${gameStats.totalMissed})`);
  }
};

// 觸控處理標記，避免重複觸發
const processedTouches = ref(new Set());

// PvP遊戲觸控開始處理 - 立即觸發，支援多點觸控
const handleTouchStart = (event, item, side) => {
  if (!gameStarted.value || gameOver.value) return;
  
  // 遍歷所有新的觸控點
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    const touchId = touch.identifier;
    
    // 防止重複處理同一個觸控點
    if (!processedTouches.value.has(touchId)) {
      processedTouches.value.add(touchId);
      
      // 立即觸發物品點擊，不等 touchend
      handleItemClick(item, side);
      
      // 延遲清除觸控標記
      setTimeout(() => {
        processedTouches.value.delete(touchId);
      }, 100);
    }
  }
  
  // 阻止預設行為，避免觸發 click 事件
  event.preventDefault();
  event.stopPropagation();
};

// 觸控結束處理 - 清理狀態
const handleTouchEnd = (event) => {
  // 清理結束的觸控點標記
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    processedTouches.value.delete(touch.identifier);
  }
};

const handleItemClick = async (item, side) => {
  if (!gameStarted.value || gameOver.value) return;

  // 取得遊戲區域和物體位置
  const gameArea = gameAreaRef.value;
  const gameRect = gameArea.getBoundingClientRect();
  
  // 使用物體的座標而非點擊位置
  // 將物體的相對座標轉換為絕對座標
  let itemX, itemY;
  
  // 根據側邊選擇正確的物體座標
  if (side === 'left') {
    // 左側物體
    itemX = item.x + gameRect.left;
    itemY = item.y + gameRect.top;
  } else {
    // 右側物體，需要調整座標
    itemX = item.x + gameRect.left + gameRect.width / 2; // 加上遊戲區域寬度的一半
    itemY = item.y + gameRect.top;
  }
  
  // 確保點擊的一側與項目的一側匹配
  const clickSide = side;
  if (clickSide !== side) return;

  // 決定分數字樣
  let effectText = '';
  let isCorrect = false;
  
  if (item.isCorrect) {
    effectText = '+10';
    isCorrect = true;
    // 播放正確音效
    if (correctSound.value) {
      correctSound.value.currentTime = 0; // 重置播放進度
      correctSound.value.play();
    }
  } else {
    effectText = '-1';
    isCorrect = false;
    // 播放錯誤音效
    if (wrongSound.value) {
      wrongSound.value.currentTime = 0; // 重置播放進度
      wrongSound.value.play();
    }
  }

  // 使用物體的位置作為飛行終點座標
  // 將分數效果顯示在物體上
  const targetPos = {
    x: itemX, // 使用物體的 X 座標
    y: itemY  // 使用物體的 Y 座標
  };

  // 新增動畫效果
  const id = flyingScoreId++;
  const effect = {
    id,
    text: effectText,
    isCorrect,
    currentX: itemX,
    currentY: itemY,
    targetX: targetPos.x,
    targetY: targetPos.y,
    opacity: 1
  };
  flyingScoreEffects.value.push(effect);

  // 動畫過程（簡單線性移動，500ms）
  const duration = 500;
  const frameRate = 1000 / 60;
  let elapsed = 0;
  function animate() {
    elapsed += frameRate;
    const t = Math.min(elapsed / duration, 1);
    effect.currentX = itemX + (targetPos.x - itemX) * t;
    effect.currentY = itemY + (targetPos.y - itemY) * t;
    effect.opacity = 1 - t * 0.5;
    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      // 動畫結束移除
      flyingScoreEffects.value = flyingScoreEffects.value.filter(e => e.id !== id);
    }
  }
  animate();

  // 移除被點擊的物品 & 分數統計
  gameStats.totalClicked++; // 記錄點擊統計
  
  if (side === 'left') {
    leftItems.value = leftItems.value.filter(i => i.id !== item.id);
    if (item.isCorrect) {
      leftScore.value += 10;
      leftCorrectCount.value += 1;
      leftCurrentCombo.value += 1;
      if (leftCurrentCombo.value > leftMaxCombo.value) {
        leftMaxCombo.value = leftCurrentCombo.value;
      }
    } else {
      leftScore.value -= 1; // 答錯扣1分
      leftWrongCount.value += 1;
      leftCurrentCombo.value = 0;
    }
  } else {
    rightItems.value = rightItems.value.filter(i => i.id !== item.id);
    if (item.isCorrect) {
      rightScore.value += 10;
      rightCorrectCount.value += 1;
      rightCurrentCombo.value += 1;
      if (rightCurrentCombo.value > rightMaxCombo.value) {
        rightMaxCombo.value = rightCurrentCombo.value;
      }
    } else {
      rightScore.value -= 1; // 答錯扣1分
      rightWrongCount.value += 1;
      rightCurrentCombo.value = 0;
    }
  }
};
// 格式化时间显示
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 切換遊戲狀態（開始/暫停/繼續）
const toggleGame = () => {
  if (!gameStarted.value) {
    if (!isCountingDown.value) {
      showReadyGoCountdown();
    }
  } else if (gamePaused.value) {
    resumeGame();
  } else {
    pauseGame();
  }
};

// 顯示 Ready, Go! 倒數效果
const showReadyGoCountdown = () => {
  // 停止並重置所有音效
  if (readySound.value) {
    readySound.value.pause();
    readySound.value.currentTime = 0;
  }
  if (goSound.value) {
    goSound.value.pause();
    goSound.value.currentTime = 0;
  }
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.currentTime = 0;
  }
  if (correctSound.value) {
    correctSound.value.pause();
    correctSound.value.currentTime = 0;
  }
  if (wrongSound.value) {
    wrongSound.value.pause();
    wrongSound.value.currentTime = 0;
  }
  if (tieSound.value) {
    tieSound.value.pause();
    tieSound.value.currentTime = 0;
  }
  if (winSound.value) {
    winSound.value.pause();
    winSound.value.currentTime = 0;
  }


  beforeGame.value = false;
  isCountingDown.value = true;
  showReadyGo.value = true;
  readyGoText.value = 'Ready';
  
  // 播放 Ready 音效
  if (readySound.value) {
    readySound.value.currentTime = 0;
    readySound.value.play();
  }
  
  // 2秒後顯示 Go!
  setTimeout(() => {
    readyGoText.value = 'Go!';
    
    // 播放 Go 音效
    if (goSound.value) {
      goSound.value.currentTime = 0;
      goSound.value.play();
    }
    
    // 再過1秒後開始遊戲
    setTimeout(() => {
      showReadyGo.value = false;
      isCountingDown.value = false;
      startGame();
    }, 1000);
  }, 2000);
};

// 開始遊戲
const startGame = () => {
  // 設定真正的遊戲開始時間（Ready, Go!結束後）
  gameStats.gameStartTime = Date.now();
  
  setRandomBackgrounds();
  
  // 初始化遊戲區域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 預先分配所有掉落物品，確保公平性
  preAllocateGameItems();
  
  // 統計時間將在遊戲真正開始時設定（在Ready, Go!之後）
  gameStats.lastReportTime = Date.now();

  // 重置遊戲狀態
  gameStarted.value = true;
  gamePaused.value = false;
  gameOver.value = false;
  leftScore.value = 0;
  rightScore.value = 0;
  timeRemaining.value = gameConfig.gameDuration;
  leftItems.value = [];
  rightItems.value = [];
  
  // 重置統計數據
  leftCorrectCount.value = 0;
  leftWrongCount.value = 0;
  leftMaxCombo.value = 0;
  rightCorrectCount.value = 0;
  rightWrongCount.value = 0;
  rightMaxCombo.value = 0;
  leftCurrentCombo.value = 0;
  rightCurrentCombo.value = 0;

  // 播放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.play();
  }

  // 啟動遊戲循環，根據遊戲速度調整更新頻率
  const updateInterval = Math.max(30, 70 - (gameConfig.gameSpeed * 10)); // 速度越快，更新越頻繁
  gameLoop = setInterval(() => {
    updateFallingItems();
  }, updateInterval);

  // 啟動計時器
  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      endGame();
    }
  }, 1000);
};

// 暫停遊戲
const pauseGame = () => {
  if (!gameStarted.value || gameOver.value) return;

  gamePaused.value = true;

  // 暫停背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
  }

  // 清除定時器
  clearInterval(gameLoop);
  clearInterval(timerInterval);
};

// 繼續遊戲
const resumeGame = () => {
  if (!gameStarted.value || gameOver.value || !gamePaused.value) return;

  gamePaused.value = false;

  // 繼續播放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.play();
  }

  // 重新啟動遊戲循環，根據遊戲速度調整更新頻率
  const updateInterval = Math.max(30, 70 - (gameConfig.gameSpeed * 10)); // 速度越快，更新越頻繁
  gameLoop = setInterval(() => {
    updateFallingItems();
  }, updateInterval);

  // 重新啟動計時器
  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      endGame();
    }
  }, 1000);
};

// 结束游戏
const endGame = () => {
  gameStarted.value = false;
  gameOver.value = true;

  // 生成詳細統計報告
  // const gameEndTime = Date.now();
  // const actualGameDuration = (gameEndTime - gameStats.gameStartTime) / 1000;
  // const remainingInQueue = itemQueues.leftQueue.length + itemQueues.rightQueue.length;
  // const itemsOnScreen = leftItems.value.length + rightItems.value.length;
  // const totalStartItems = gameStats.leftQueueStartSize + gameStats.rightQueueStartSize;
  // const totalProcessed = gameStats.totalClicked + gameStats.totalMissed;
  // const completionRate = ((totalProcessed) / totalStartItems * 100).toFixed(1);
  
  // console.log(`\n🏁 遊戲結束統計報告`);
  // console.log(`⏱️ 設定時長: ${gameConfig.gameDuration}秒 | 實際時長: ${actualGameDuration.toFixed(1)}秒`);
  // console.log(`📦 預分配物品: ${totalStartItems}項 (左${gameStats.leftQueueStartSize} + 右${gameStats.rightQueueStartSize})`);
  // console.log(`🎯 已生成: ${gameStats.totalGenerated}項`);
  // console.log(`👆 玩家點擊: ${gameStats.totalClicked}項`);
  // console.log(`💧 掉落遺失: ${gameStats.totalMissed}項`);
  // console.log(`📺 螢幕剩餘: ${itemsOnScreen}項`);
  // console.log(`🗃️ 佇列剩餘: ${remainingInQueue}項`);
  // console.log(`📊 完成率: ${completionRate}%`);
  // console.log(`${remainingInQueue > 0 ? '⚠️ 警告：還有物品未掉落！' : '✅ 所有物品已掉落完畢'}`);

  // 停止背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.currentTime = 0;
  }
  
  // 根據遊戲結果播放相應音效
  setTimeout(() => {
    if (leftScore.value === rightScore.value) {
      // 平局
      if (tieSound.value) {
        tieSound.value.currentTime = 0;
        tieSound.value.play();
      }
    } else {
      // 有人獲勝
      if (winSound.value) {
        winSound.value.currentTime = 0;
        winSound.value.play();
      }
    }
  }, 500); // 延遲半秒再播放，讓畫面先切換

  // 清除定时器
  clearInterval(gameLoop);
  clearInterval(timerInterval);
};

// 重置游戏
const resetGame = () => {
  setRandomBackgrounds();
  gameOver.value = false;
  leftItems.value = [];
  rightItems.value = [];
  beforeGame.value = true;
  gamePaused.value = false;
  
  // 清理觸控狀態
  processedTouches.value.clear();
  
  // 清空物品佇列
  itemQueues.leftQueue = [];
  itemQueues.rightQueue = [];
  
  // 重置計數器
  itemCounter.leftCorrect = 0;
  itemCounter.leftError = 0;
  itemCounter.rightCorrect = 0;
  itemCounter.rightError = 0;
};

// 前往設置頁面
const goToSettings = () => {
  router.push('/ht-pvpgame-setting');
};

// 推送到終端
const sendToWebIRS = () => {
    messageBridge.postMessage({action: 'SendGame.WebIRS', data: {game: 'pvpGame', gameConfig: JSON.stringify(gameConfig)}});
};

// 處理推送按鈕點擊
const handleSendClick = () => {
    console.log('按鈕點擊，當前禁用狀態：', isSendDisabled.value);
    
    if (isSendDisabled.value) {
        iviewMessage.warning({
            content: t('PvPgame.sendCooldown'),
            duration: 3,
            closable: true
        });
        return;
    }
    
    // 執行推送
    sendToWebIRS();
    iviewMessage.success({
        content: t('PvPgame.sendSuccess'),
        duration: 3,
        closable: true
    });
    
    // 設置冷卻狀態
    isSendDisabled.value = true;
    console.log('設置為禁用狀態：', isSendDisabled.value);
    
    // 1分鐘後恢復
    cooldownTimer.value = setTimeout(() => {
        isSendDisabled.value = false;
        console.log('恢復為啟用狀態：', isSendDisabled.value);
    }, 60000); // 60秒
};

// 组件挂载时
onMounted(() => {
  // 初始化游戏区域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 设置初始时间
  timeRemaining.value = gameConfig.gameDuration;

  // 初始化背景音樂
  backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/happy-go-lucky-full-length-201627.mp3'));
  backgroundMusic.value.loop = true;
  backgroundMusic.value.volume = 0.5; // 設置音量為50%
  
  // 初始化正確和錯誤音效
  correctSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/shine-6-268910.mp3'));
  correctSound.value.volume = 0.6;
  
  wrongSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/wrong-answer-129254.mp3'));
  wrongSound.value.volume = 0.6;
  
  // 初始化平局和獲勝音效
  tieSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/game-176807.mp3'));
  tieSound.value.volume = 0.7;
  
  winSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
  winSound.value.volume = 0.7;
  
  // 初始化 Ready 和 Go 音效
  readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
  readySound.value.volume = 1;
  
  goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));
  goSound.value.volume = 1;
  // 設置 Go 音效的開始時間，跳過 Ready 部分
  goSound.value.currentTime = 0; // 根據音檔的實際情況調整
});

// 组件卸载时清除定时器
onUnmounted(() => {
  clearInterval(gameLoop);
  clearInterval(timerInterval);
  clearTimeout(cooldownTimer.value);
  
  // 清理觸控狀態
  processedTouches.value.clear();
  
  // 停止並釋放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.src = '';
    backgroundMusic.value = null;
  }
  
  // 釋放正確和錯誤音效
  if (correctSound.value) {
    correctSound.value.src = '';
    correctSound.value = null;
  }
  
  if (wrongSound.value) {
    wrongSound.value.src = '';
    wrongSound.value = null;
  }
  
  // 釋放平局和獲勝音效
  if (tieSound.value) {
    tieSound.value.src = '';
    tieSound.value = null;
  }
  
  if (winSound.value) {
    winSound.value.src = '';
    winSound.value = null;
  }
  
  // 釋放 Ready 和 Go 音效
  if (readySound.value) {
    readySound.value.src = '';
    readySound.value = null;
  }
  
  if (goSound.value) {
    goSound.value.src = '';
    goSound.value = null;
  }
});
</script>

<style lang="less" scoped>
@import '../assets/HTwbActKitCSS/HTpvpGame.less';

// 推送按鈕樣式
.push-button {
    width: 60px;
    height: 60px;
    background-color: #2196F3; /* 藍色 */
    border: none;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s, background-color 0.2s;
    
    .sendweb-icon{
      width: 30px !important;
      height: 30px !important;
        *{
            fill: white !important;
        }
    }

    &:hover:not(.disabled) {
        transform: scale(1.1);
        background-color: #1976D2;
    }
}

.push-button.disabled {
    background-color: #9e9e9e !important; /* 灰色，使用 !important 確保優先級 */
    cursor: not-allowed !important;
    
    .sendweb-icon {
        * {
            fill: #666666 !important;
        }
    }
}
</style>
